import { useState, useContext, useRef, useEffect } from "react"
import _ from "lodash"
import { useQuery, useMutation } from "@apollo/client"
import { Link, useParams, useNavigate } from "react-router-dom"
import { LoaderMedium } from "../../components/Loaders"
import { Formik, Form } from "formik"
import { PickList } from "primereact/picklist"
import * as Yup from "yup"
import handleBlkSubmitValues from "./helpers/handleSubmitBlkValues"
import { ADD_BLOCK, GET_PARENT_BLOCK, GET_BLOCKS_LIBRARY } from "./queries"
import itemTemplate from "./helpers/pickListItemTemplate"
import { trimTextValue } from "../../components/FieldTypes/helpers/trimTextValue"

// Context
import { UserContext } from "../../context/userContext"

// Prime
import { Messages } from "primereact/messages"
import { InputText as Input } from "primereact/inputtext"
import { Button } from "primereact/button"
import { Dropdown } from "primereact/dropdown"
import { ToggleButton } from "primereact/togglebutton"

// Styles
import styles from "../../styles/Form.module.css"
import "./PickList.css"

export default function Clone() {
  const [loading, setLoading] = useState(true)
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()

  const errMsg = useRef(null)

  const [addBlock] = useMutation(ADD_BLOCK)

  const [sysRequiedFields, setSysRequiedFields] = useState([])
  const [source, setSource] = useState([])
  const [target, setTarget] = useState([])

  const onChange = (event) => {
    const candidateRmField = _.difference(target, event.target)
    if (!_.isEmpty(candidateRmField)) {
      const { idField, field_name } = candidateRmField[0]
      if (_.includes(sysRequiedFields, idField)) {
        return errMsg.current.show([
          {
            severity: "error",
            detail: `${field_name} is a System Required Field.`,
          },
        ])
      }
    }
    setSource(event.source)
    setTarget(event.target)
  }

  const { idBlock } = useParams()

  const { data } = useQuery(GET_PARENT_BLOCK, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
      enterprise_id: userCtx.session.user.enterprise_id,
      idBlock: idBlock,
      type: "system",
    },
    onCompleted: (data) => {
      const customFields = data.customFields || []
      const systemFields = data.systemFields || []

      const customFieldsWithoutParent = _.reduce(
        customFields,
        (acc, field) => {
          const { field_parent } = field
          const fieldParent = field_parent.idField || null
          if (!fieldParent) {
            return [...acc, field]
          }
          return acc
        },
        []
      )
      const systemFieldsFiltered = _.reduce(
        systemFields,
        (acc, field) => {
          const skipField =
            field.field_type === "hidden" || field.field_type === "sys"
          if (skipField) return acc
          return [...acc, field]
        },
        []
      )
      const allFields = [...customFieldsWithoutParent, ...systemFieldsFiltered]
      const availableFields = _.difference(allFields, data.currentBlock.fields)
      const sysRequiedFieldsList = _.reduce(
        data.currentBlock.fields,
        (acc, field) => {
          const { system_required } = field
          return system_required ? [...acc, field.idField] : acc
        },
        []
      )
      setSysRequiedFields(sysRequiedFieldsList)
      setSource(availableFields)
      setTarget(data.currentBlock.fields)
      setLoading(false)
    },
  })

  // Check if allow_custom_config is true
  const allowCustomConfig = data?.currentBlock?.configuration?.allow_custom_config
  const isSystemRequiredBlock = data?.currentBlock?.configuration?.system_required

  useEffect(() => {
    if (data && !allowCustomConfig) {
      // If the block does not allow custom config, redirect to library
      navigate("/library/blocks")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (loading) return <LoaderMedium />

  // also prevent rendering the rest of the component if redirecting
  if (data && !allowCustomConfig) {
    return null
  }

  const newBlock = Yup.object().shape({
    block_title: Yup.string(),
  })

  const behaviorOptions = [
    { label: "Update revision", value: true },
    { label: "Create new", value: false },
  ]

  return (
    <div>
      <h1>Customize Block</h1>
      <Link
        to="/library/blocks"
        style={{ marginBottom: 30, display: "inline-block" }}
        className="btn"
      >
        Back to Library
      </Link>
      <Formik
        initialValues={{
          block_name: data.currentBlock.block_name,
          block_title: data.currentBlock.block_title,
          block_parent: idBlock,
          data_propogation:
            data.currentBlock.configuration.behavior.data_propagation,
          block_required: data.currentBlock.configuration.system_required,
        }}
        validationSchema={newBlock}
        onSubmit={async (values) => {
          const fields = _.map(target, ({ idField, field_name }) => {
            return {
              idField,
              field_name,
            }
          })
          await addBlock({
            variables: {
              organization_id: userCtx.session.user.organization_id,
              enterprise_id: userCtx.session.user.enterprise_id,
              created_by: userCtx.session.user.staff_id,
              updated_by: userCtx.session.user.staff_id,
              block_parent: idBlock,
              ...handleBlkSubmitValues(values, fields),
            },
            refetchQueries: [
              {
                query: GET_BLOCKS_LIBRARY,
                variables: {
                  staff_id: userCtx.session.user.staff_id,
                  organization_id: userCtx.session.user.organization_id,
                  enterprise_id: userCtx.session.user.enterprise_id,
                },
              },
            ],
          })
          navigate("/library/blocks")
        }}
      >
        {(formik) => {
          return (
            <Form className={styles.form}>
              <div className={styles.field}>
                <label className={styles.label}>Block Title:</label>
                <p className={styles.description}>
                  Provide the display name for this Block used in forms and reports.
                </p>
                <Input
                  name="block_title"
                  onChange={(e) => formik.handleChange(trimTextValue(e))}
                  onBlur={formik.handleBlur}
                  value={formik.values.block_title}
                  className={styles.value}
                />
                {formik.touched.block_title && formik.errors.block_title && (
                  <div className={styles.errFieldMsg}>
                    {formik.errors.block_title}
                  </div>
                )}
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Block Behavior:</label>
                <p className={styles.description}>
                  Block Behavior cannot be changed on an existing Block definition.
                </p>
                <Dropdown
                  name="data_propogation"
                  value={formik.values.data_propogation}
                  options={behaviorOptions}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled
                  className={styles.value}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Is required:</label>
                <p className={styles.description}>
                  {formik.values.block_required === true ?
                    "Once required, a Block may not be made optional." :
                    "If required, users must complete or review this Block on every form it is placed in."
                  }
                </p>
                <ToggleButton
                  name="block_required"
                  checked={formik.values.block_required}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onIcon="pi pi-check"
                  offIcon="pi pi-times"
                  aria-label="block_required"
                  disabled={isSystemRequiredBlock}
                  className={styles.value}
                />
              </div>

              <div>
                <label className={styles.label}>Fields:</label>
                <p className={styles.description}>
                  Administrators can use the selector below to modify and update
                  the existing application block. From the Available Fields
                  space, select any fields that should be added or removed from
                  the current block. Any updates made and saved to blocks will
                  be automatically reflected in all application templates where
                  that block appears.
                </p>
                <div style={{ margin: "10px 0" }}>
                  <Messages ref={errMsg} />
                </div>
              </div>

              <div className="picklist">
                <PickList
                  source={source}
                  target={target}
                  itemTemplate={itemTemplate}
                  sourceHeader="Available Fields"
                  targetHeader="Selected and Order"
                  sourceStyle={{ height: "342px" }}
                  targetStyle={{ height: "342px" }}
                  onChange={onChange}
                  style={{ fontSize: "small" }}
                />
              </div>

              <Button
                style={{ marginTop: 15 }}
                label="Submit"
                icon="pi pi-check"
                loading={formik.isSubmitting}
                disabled={!formik.isValid}
                type="submit"
                className={styles.btnSubmit}
              />
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}
